import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import RecordRTC from "recordrtc";
import {
  apiGetPeriodicMaintenanceAvailability,
  apiGetPeriodicMaintenanceSchedule,
  fetchMaintenanceFormMasterData,
} from "@app/api";
import { DataDialog } from "@app/components/atoms";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import type {
  IrregularMaintenanceItemChoiceMaster,
  MaintenanceCategoryMaster,
  MaintenanceSelectOption,
  MaintenanceTypeMaster,
  PeriodicMaintenanceSchedule,
} from "@app/types/maintenanceMasterData.types";
import { IconPlusBlack } from "@app/components/icons/IconPlusBlack/IconPlusBlack";
import { MaintenanceItemEditorDialog } from "./MaintenanceItemEditorDialog";
import {
  arePeriodicMaintenanceFieldsValid,
  countMaintenanceItemsMediaTotal,
  isScheduledMaintenanceItem,
  MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
  MAINTENANCE_SELECT_FORM_CONTROL_SX,
  MAINTENANCE_SELECT_MENU_PROPS,
  isManualMaintenanceItemRefillAmountInvalid,
  MaintenanceItemAction,
  ManualMaintenanceItemEntry,
  ScheduledMaintenanceItemEntry,
  type MaintenanceItemEntry,
} from "./maintenanceItemShared";

import {
  submitCreateMaintenance,
  submitCreateMaintenanceDraft,
  submitUpdateMaintenance,
} from "./createMaintenanceSubmit.helper";
import {
  setIsLoading,
  setIsUpdatedMachineReportResponses,
  setShowAlert,
} from "@app/store";
import "@app/pages/Features/MachineDetail/Components/InspectionHistory/recording.css";
import {
  isWorkAtInValidRange,
  WORK_AT_MIN_DATE,
} from "../Refuel/createRefuelSubmit.helper";
import { SignatureDialog } from "../SignatureDialog";
import { getGroupIsSignatureRequired } from "../groupSignature.helper";
import { useAppContext } from "@app/AppContext";
import { MaintenanceNonNumericItemRow } from "./CreateMaintenanceItemRows";
import { MaintenanceReportType, MaintenanceResultType } from "@app/constants";
import { TemplateInspectionItemMedia } from "../Inspection/CreateInspections";
import {
  dateLocales,
  NUMERIC_SMR_MAX,
  isNumericValueValid,
  NUMERIC_ODO_MAX,
  sanitizeNumericInputValue,
  normalizeNumericOnBlur,
  getOdometerLabel,
  cloneBlobForPlayback,
  getTemplateMediaPreviewUrl,
  isTemplateMediaVideo,
  openImageInViewer,
  formatIntervalMaintenanceLabel,
} from "./createMaintenance.utils";
import CheckIcon from "@mui/icons-material/Check";
import { MachineReportMedia, Report } from "@app/types";

type Props = {
  open: boolean;
  onClose: () => void;
  groupId: string;
  machineId: string;
  maintenanceType: MaintenanceReportType;
  mode?: "create" | "edit" | "draft";
  editingReport?: any;
  onUpdateSuccess?: () => void;
  prefetchedPeriodicScheduleAvailable?: boolean | null;
  machineModelType?: string;
};

function mapReportToEntries(report: Report): MaintenanceItemEntry[] {
  return report.items.map((item) => {
    const common = {
      id: item.maintenanceReportItemResponseId ?? crypto.randomUUID(),
      maintenanceReportItemResponseId:
        item.maintenanceReportItemResponseId ?? undefined,
      comment: item.comment ?? "",
      resultValue: item.result ?? undefined,
      commentExpanded: false,
      recordedBlob: null,
      mediaFiles: [] as File[],
      existingItemMedias: item.itemMedias ?? [],
      existingItemRecording: item.itemRecordings?.[0] ?? null,
    };

    if (item.periodicMaintenanceMachineItemId) {
      return {
        ...common,
        source: "schedule" as const,
        periodicMaintenanceItemId: "",
        periodicMaintenanceMachineItemId: item.periodicMaintenanceMachineItemId,
        itemName: item.maintenanceItemName ?? "",
        work: item.work ?? "",
        workCode: "",
        regularMaintenanceItemChoiceId:
          item.regularMaintenanceItemChoiceId ?? "",
        regularMaintenanceItemChoiceCode: "",
        regularMaintenanceItemChoiceName: "",
      } satisfies ScheduledMaintenanceItemEntry;
    }

    return {
      ...common,
      source: "manual" as const,
      categoryId: item.maintenanceCategoryId ?? "",
      categoryCode: "", // backfilled once maintenanceCategories master data loads
      categoryLabel: item.maintenanceCategoryName ?? "",
      maintenanceTypeId: item.maintenanceTypeId ?? "",
      maintenanceTypeCode: "", // no equivalent in GET response; backfill if needed, see note below
      maintenanceTypeLabel: item.maintenanceTypeName ?? "",
      maintenanceItemId: item.maintenanceItemId ?? "",
      maintenanceItemLabel: item.maintenanceItemName ?? "",
      maintenanceItemOther: item.maintenanceItemOther ?? "",
      refillAmount: item.refillAmount != null ? String(item.refillAmount) : "",
      action: item.isReplace
        ? ("replace" as MaintenanceItemAction)
        : item.isRefill
          ? ("refill" as MaintenanceItemAction)
          : "",
    } satisfies ManualMaintenanceItemEntry;
  });
}

const initialFormState = (seed?: Report) => {
  let topInterval;
  let intervals;
  if (seed?.isPeriodicMaintenance) {
    intervals = seed?.intervalMaintenanceItemChoices;
    const intervalLength = (intervals?.length ?? 0) - 1;
    const lastInterval =
      intervalLength >= 0 ? intervals?.[intervalLength] : undefined;
    topInterval =
      lastInterval?.intervalMaintenanceItemChoiceCode === "OTHER"
        ? intervals?.[intervalLength - 1]?.intervalMaintenanceItemChoiceId
        : lastInterval?.intervalMaintenanceItemChoiceId;
  }
  return {
    maintenanceItems: seed
      ? mapReportToEntries(seed)
      : ([] as MaintenanceItemEntry[]),
    periodicSwitchOn: seed?.isPeriodicMaintenance ?? false,
    maintenanceIntervalIds: topInterval ? [topInterval] : [],
    intervalMaintenanceItemChoices: intervals ?? null,
    workScopeId: "",
    periodicFieldsTouched: false,
    serviceMeterValue:
      seed?.serviceMeterInHour != null ? String(seed.serviceMeterInHour) : "",
    serviceMeterTouched: false,
    odometerValue: seed?.odometer != null ? String(seed.odometer) : "",
    odometerTouched: false,
    workAt: seed?.workAt ? new Date(seed.workAt) : new Date(),
    workAtTouched: false,
    fileCount: 0,
    hasUploadError: false,
  };
};

export function CreateMaintenance({
  open,
  onClose,
  groupId,
  machineId,
  maintenanceType,
  mode = "create",
  editingReport = null,
  onUpdateSuccess,
  prefetchedPeriodicScheduleAvailable = null,
  machineModelType: machineModelTypeProp,
}: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    userTimeFormat,
    machineModelType: contextMachineModelType,
    isoLocaleCode,
  } = useAppContext();
  const machineModelType = machineModelTypeProp || contextMachineModelType;
  const dateLocale = dateLocales[i18n.language] ?? enUS;
  const isLoadedFromExistingReport = mode === "edit" || mode === "draft";
  const [form, setForm] = useState(() =>
    initialFormState(
      isLoadedFromExistingReport ? (editingReport ?? undefined) : undefined,
    ),
  );
  const [draftMachineReportId, setDraftMachineReportId] = useState<
    string | null
  >(mode === "draft" ? (editingReport?.machineReportId ?? null) : null);

  const [mediaPreviewNonce, setMediaPreviewNonce] = useState(0);

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<ReturnType<typeof RecordRTC> | null>(
    null,
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [recordingInterval, setRecordingInterval] = useState<ReturnType<
    typeof setInterval
  > | null>(null);
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const stoppedRef = useRef(false);
  const skipNextRecordingBlobRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<ReturnType<typeof RecordRTC> | null>(null);

  const [openFormNoMic, setOpenFormNoMic] = useState(false);
  const [openFormDeleteRecording, setOpenFormDeleteRecording] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [openAddMaintenanceItemDialog, setOpenAddMaintenanceItemDialog] =
    useState(false);
  const [addItemCategoryId, setAddItemCategoryId] = useState("");
  const [openMaintenanceItemEditor, setOpenMaintenanceItemEditor] =
    useState(false);
  const [editingMaintenanceItem, setEditingMaintenanceItem] =
    useState<ManualMaintenanceItemEntry | null>(null);
  const [presetItemEditorCategoryId, setPresetItemEditorCategoryId] =
    useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [masterDataError, setMasterDataError] = useState<string | null>(null);
  const [intervalOptions, setIntervalOptions] = useState<
    MaintenanceSelectOption[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    MaintenanceSelectOption[]
  >([]);
  const [irregularMaintenanceItems, setIrregularMaintenanceItems] = useState<
    IrregularMaintenanceItemChoiceMaster[]
  >([]);
  const [maintenanceCategories, setMaintenanceCategories] = useState<
    MaintenanceCategoryMaster[]
  >([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<
    MaintenanceTypeMaster[]
  >([]);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const recordingStartTimeMs = recordingStartTimeRef.current;
  const [recordingForItemId, setRecordingForItemId] = useState<string | null>(
    null,
  );
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingDeleteRecordingItemIdRef = useRef<string | null>(null);

  const recordingTargetItemIdRef = useRef<string | null>(null);
  const [scheduleItems, setScheduleItems] =
    useState<PeriodicMaintenanceSchedule | null>(null);
  const [scheduleItemsLoading, setScheduleItemsLoading] = useState(false);
  const [intervalTouched, setIntervalTouched] = useState(false);
  const [periodicScheduleAvailable, setPeriodicScheduleAvailable] = useState<
    boolean | null
  >(null);
  const [editInitialize, setEditInitialize] = useState(false);

  const releaseMicrophone = useCallback(() => {
    const stopStreamTracks = (stream: MediaStream | null | undefined) => {
      stream?.getTracks?.().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    };
    stopStreamTracks(mediaStreamRef.current);
    stopStreamTracks(recorderRef.current?.stream);
    mediaStreamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    const activeRecorder = recorderRef.current;
    if (!activeRecorder) {
      releaseMicrophone();
      return;
    }

    stoppedRef.current = true;

    setIsRecording(false);
    setIsPaused(false);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    const targetId = recordingTargetItemIdRef.current;

    releaseMicrophone();

    activeRecorder.stopRecording(() => {
      window.setTimeout(() => {
        const raw = activeRecorder.getBlob() as Blob | undefined;
        const skip = skipNextRecordingBlobRef.current;
        skipNextRecordingBlobRef.current = false;

        if (!skip && raw && raw.size > 44 && targetId) {
          const playable = cloneBlobForPlayback(raw);
          setForm((prev) => ({
            ...prev,
            maintenanceItems: prev.maintenanceItems.map((item) =>
              item.id === targetId ? { ...item, recordedBlob: playable } : item,
            ),
          }));
        }

        setRecorder(null);
        recorderRef.current = null;
        recordingStartTimeRef.current = null;
        setRecordingForItemId(null);
        recordingTargetItemIdRef.current = null;
      }, 300);
    });
  }, [releaseMicrophone]);

  const setItemRef = useCallback(
    (itemId: string, el: HTMLDivElement | null) => {
      itemRefs.current[itemId] = el;
    },
    [],
  );
  const totalInspectionMediaCount = useMemo(
    () => countMaintenanceItemsMediaTotal(form.maintenanceItems),
    [form.maintenanceItems],
  );
  const patchDraft = useCallback(
    (itemId: string, patch: Partial<ManualMaintenanceItemEntry>) => {
      setForm((prev) => ({
        ...prev,
        maintenanceItems: prev.maintenanceItems.map((item) =>
          item.id === itemId
            ? ({ ...item, ...patch } as MaintenanceItemEntry)
            : item,
        ),
      }));
    },
    [],
  );

  const handleToggleCommentSection = useCallback((itemId: string) => {
    setForm((prev) => ({
      ...prev,
      maintenanceItems: prev.maintenanceItems.map((item) =>
        item.id === itemId
          ? { ...item, commentExpanded: !item.commentExpanded }
          : item,
      ),
    }));
  }, []);

  const handleResultIconPick = useCallback(
    (itemId: string, value: MaintenanceResultType | undefined) => {
      setValidationAttempted(true);
      setForm((prev) => ({
        ...prev,
        maintenanceItems: prev.maintenanceItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                resultValue: value,
              }
            : item,
        ),
      }));
      if (value) {
        requestAnimationFrame(() => {
          itemRefs.current[itemId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    },
    [],
  );

  const startRecordingForItem = useCallback(
    async (itemId: string) => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMic = devices.some((device) => device.kind === "audioinput");

        if (!hasMic) {
          setOpenFormNoMic(true);
          return;
        }

        releaseMicrophone();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        mediaStreamRef.current = stream;

        recordingTargetItemIdRef.current = itemId;
        setRecordingForItemId(itemId);

        setForm((prev) => ({
          ...prev,
          maintenanceItems: prev.maintenanceItems.map((item) =>
            item.id === itemId ? { ...item, recordedBlob: null } : item,
          ),
        }));

        setIsPaused(false);
        stoppedRef.current = false;

        const newRecorder = new RecordRTC(stream, {
          type: "audio",
          mimeType: "audio/webm",
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: 16000,
          audioBitsPerSecond: 64000,
        });

        recorderRef.current = newRecorder;
        recordingStartTimeRef.current = Date.now();
        newRecorder.startRecording();
        setRecorder(newRecorder);
        setIsRecording(true);

        const timeout = setTimeout(() => {
          stopRecording();
        }, 59200);

        recordingTimeoutRef.current = timeout;
      } catch (err) {
        console.warn("Microphone access denied", err);
        releaseMicrophone();
        setMediaAvailable(false);
        setRecordingForItemId(null);
        recordingTargetItemIdRef.current = null;
      }
    },
    [releaseMicrophone, stopRecording],
  );

  const requestDeleteRecording = useCallback((itemId: string) => {
    pendingDeleteRecordingItemIdRef.current = itemId;
    setOpenFormDeleteRecording(true);
  }, []);

  const openTemplateMediaPreview = useCallback(
    (media: TemplateInspectionItemMedia) => {
      const url = getTemplateMediaPreviewUrl(media);
      if (!url) return;
      if (isTemplateMediaVideo(media)) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      openImageInViewer(url, setSelectedImage, setViewerOpen);
    },
    [],
  );

  const handleMediaFilesChange = useCallback(
    (itemId: string, mergedList: Array<File | MachineReportMedia>) => {
      setForm((prev) => {
        const nextExisting: MachineReportMedia[] = [];
        const nextFiles: File[] = [];
        for (const entry of mergedList) {
          if (entry instanceof File) {
            nextFiles.push(entry);
          } else {
            nextExisting.push(entry);
          }
        }

        const otherItemsMediaCount = prev.maintenanceItems.reduce(
          (sum, item) =>
            item.id === itemId
              ? sum
              : sum +
                (item.mediaFiles?.length ?? 0) +
                (item.existingItemMedias?.length ?? 0),
          0,
        );
        const maxForThisItem = Math.min(
          5,
          Math.max(
            0,
            MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT - otherItemsMediaCount,
          ),
        );
        const cappedFiles = nextFiles.slice(
          0,
          Math.max(0, maxForThisItem - nextExisting.length),
        );

        return {
          ...prev,
          maintenanceItems: prev.maintenanceItems.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  existingItemMedias: nextExisting,
                  mediaFiles: cappedFiles,
                  fileCount: nextExisting.length + cappedFiles.length,
                  hasUploadError: false,
                }
              : item,
          ),
        };
      });
    },
    [],
  );

  const handleFileCountChange = useCallback(
    (itemId: string, count: number | ((prev: number) => number)) => {
      setForm((prev) => ({
        ...prev,
        maintenanceItems: prev.maintenanceItems.map((item) => {
          if (item.id !== itemId) return item;
          const currentCount = item.fileCount ?? item.mediaFiles.length;
          const nextCount =
            typeof count === "function" ? count(currentCount) : count;
          return { ...item, fileCount: nextCount };
        }),
      }));
    },
    [],
  );

  const handleUploadErrorChange = useCallback(
    (itemId: string, hasError: boolean) => {
      setForm((prev) => ({
        ...prev,
        maintenanceItems: prev.maintenanceItems.map((item) =>
          item.id === itemId ? { ...item, hasUploadError: hasError } : item,
        ),
      }));
    },
    [],
  );

  const patchForm = useCallback(
    (patch: Partial<ReturnType<typeof initialFormState>>) => {
      setForm((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const resetRecordingGlobals = useCallback(() => {
    if (recordingInterval) clearInterval(recordingInterval);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    releaseMicrophone();
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    setRecorder(null);
    recorderRef.current = null;
    setRecordingInterval(null);
    setMediaAvailable(true);
    recordingStartTimeRef.current = null;
    stoppedRef.current = false;
  }, [recordingInterval, releaseMicrophone]);

  const resetForm = useCallback(() => {
    setForm(initialFormState());
    setMediaPreviewNonce((n) => n + 1);
    resetRecordingGlobals();
    setDraftMachineReportId(null);
    setValidationAttempted(false);
  }, [resetRecordingGlobals]);

  function MaintenanceScheduleItemsSkeleton() {
    return (
      <>
        {[0, 1].map((groupIdx) => (
          <Box key={groupIdx} sx={{ mb: 3 }}>
            <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
            <Box
              sx={{
                borderRadius: 1,
                bgcolor: "#fff",
                px: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              {[0, 1].map((itemIdx) => (
                <Box key={itemIdx}>
                  {itemIdx > 0 && (
                    <Box
                      component="hr"
                      sx={{
                        border: "none",
                        borderTop: "1px solid #e0e0e0",
                        m: 0,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      py: 1.25,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Skeleton variant="text" width="55%" height={20} />
                      <Box sx={{ display: "flex", gap: "20px", flexShrink: 0 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </Box>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={20}
                      sx={{ borderRadius: 1, my: 4 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </>
    );
  }

  const OTHER_GROUP_KEY = "__manual_other__";

  const itemGroups = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; code: string; items: MaintenanceItemEntry[] }
    >();

    for (const item of form.maintenanceItems) {
      if (isScheduledMaintenanceItem(item)) {
        const key = item.regularMaintenanceItemChoiceId;
        const intervalData = form.intervalMaintenanceItemChoices?.find(
          (value) => value.intervalMaintenanceItemChoiceId === key,
        );
        if (!groups.has(key)) {
          groups.set(key, {
            name:
              item.regularMaintenanceItemChoiceName ||
              (mode === "edit" || mode === "draft"
                ? (intervalData?.intervalMaintenanceItemChoiceName ?? "")
                : ""),
            code:
              item.regularMaintenanceItemChoiceCode ||
              (mode === "edit" || mode === "draft"
                ? (intervalData?.intervalMaintenanceItemChoiceCode ?? "")
                : ""),
            items: [],
          });
        }
        groups.get(key)!.items.push(item);
      } else {
        if (!groups.has(OTHER_GROUP_KEY)) {
          groups.set(OTHER_GROUP_KEY, {
            name: t("dialogMaintenanceReports.registeredMaintenance"),
            code: "OTHER",
            items: [],
          });
        }
        groups.get(OTHER_GROUP_KEY)!.items.push(item);
      }
    }

    const entries = Array.from(groups.entries());
    entries.sort((a, b) =>
      a[0] === OTHER_GROUP_KEY ? 1 : b[0] === OTHER_GROUP_KEY ? -1 : 0,
    );

    return entries.map(([intervalId, group]) => ({ intervalId, ...group }));
  }, [form.maintenanceItems, t]);

  useEffect(() => {
    if (!open) {
      resetForm();
      setMasterDataError(null);
      setIntervalOptions([]);
      setCategoryOptions([]);
      setIrregularMaintenanceItems([]);
      setMaintenanceCategories([]);
      setMaintenanceTypes([]);
      return;
    }
    if ((mode === "edit" || mode === "draft") && editingReport) {
      setForm(initialFormState(editingReport));
      if (mode === "draft") {
        setDraftMachineReportId(editingReport.machineReportId ?? null);
      }
      setValidationAttempted(true);
    }
  }, [open, mode, editingReport, resetForm]);

  useEffect(() => {
    if (
      (mode !== "edit" && mode !== "draft") ||
      maintenanceCategories.length === 0
    ) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      maintenanceItems: prev.maintenanceItems.map((item) => {
        if (item.source !== "manual" || item.categoryCode) return item;
        const category = maintenanceCategories.find(
          (c) => c.maintenanceCategoryId === item.categoryId,
        );
        return category
          ? { ...item, categoryCode: category.maintenanceCategoryCode }
          : item;
      }),
    }));
  }, [mode, maintenanceCategories]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setMasterDataLoading(true);
    setMasterDataError(null);

    fetchMaintenanceFormMasterData()
      .then(
        ({
          intervalOptions: intervals,
          workScopeOptions: scopes,
          categoryOptions: categories,
          maintenanceCategories: categoriesMasterList,
          maintenanceTypes: typesMasterList,
          irregularMaintenanceItems: irregularItems,
        }) => {
          if (cancelled) {
            return;
          }
          setIntervalOptions(intervals);
          setCategoryOptions(categories);
          setMaintenanceCategories(categoriesMasterList);
          setMaintenanceTypes(typesMasterList);
          setIrregularMaintenanceItems(irregularItems);
        },
      )
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        console.error("Failed to load maintenance master data:", error);
        setMasterDataError(t("errNetwork.content"));
        setIntervalOptions([]);
        setCategoryOptions([]);
        setIrregularMaintenanceItems([]);
        setMaintenanceCategories([]);
        setMaintenanceTypes([]);
      })
      .finally(() => {
        if (!cancelled) {
          setMasterDataLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    const isPeriodic =
      maintenanceType === MaintenanceReportType.PERIODIC_MAINTENANCE;
    if (!open || !isPeriodic) {
      return;
    }

    if (prefetchedPeriodicScheduleAvailable !== null) {
      setPeriodicScheduleAvailable(prefetchedPeriodicScheduleAvailable);
      return;
    }

    let cancelled = false;
    setPeriodicScheduleAvailable(null);

    apiGetPeriodicMaintenanceAvailability(machineModelType)
      .then((available: boolean) => {
        if (cancelled) {
          return;
        }
        setPeriodicScheduleAvailable(available);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        console.error(
          "Failed to load periodic maintenance availability:",
          error,
        );
        setPeriodicScheduleAvailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    maintenanceType,
    machineModelType,
    prefetchedPeriodicScheduleAvailable,
  ]);

  useEffect(() => {
    if (
      form.maintenanceIntervalIds.length === 1 &&
      (mode === "edit" || mode === "draft") &&
      !editInitialize
    ) {
      setEditInitialize(true);
      return;
    }
    if (form.maintenanceIntervalIds.length === 0) {
      return;
    }
    if (!intervalTouched) setIntervalTouched(true);
    if (periodicScheduleAvailable === false) {
      setScheduleItems({ workItems: [] });
      return;
    }
    let cancelled = false;
    setScheduleItemsLoading(true);
    const intervalName =
      intervalOptions.find(
        (option) => option.id === form.maintenanceIntervalIds[0],
      )?.code ?? "";

    apiGetPeriodicMaintenanceSchedule(machineModelType, intervalName)
      .then((workItems: PeriodicMaintenanceSchedule) => {
        if (cancelled) {
          return;
        }
        setScheduleItems(workItems);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        console.error("Failed to load maintenance master data:", error);
        setScheduleItems(null);
      })
      .finally(() => {
        if (!cancelled) {
          setScheduleItemsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.maintenanceIntervalIds]);

  useEffect(() => {
    if (!scheduleItems) {
      return;
    }
    const scheduledFromApi: ScheduledMaintenanceItemEntry[] =
      scheduleItems.workItems.flatMap((interval) =>
        interval.items.map((it) => ({
          source: "schedule" as const,
          id: it.periodicMaintenanceMachineItemId,
          periodicMaintenanceItemId: it.periodicMaintenanceItemId,
          periodicMaintenanceMachineItemId: it.periodicMaintenanceMachineItemId,
          itemName: it.itemName,
          work: it.work,
          workCode: it.workCode,
          regularMaintenanceItemChoiceId:
            interval.regularMaintenanceItemChoiceId,
          regularMaintenanceItemChoiceCode:
            interval.regularMaintenanceItemChoiceCode,
          regularMaintenanceItemChoiceName:
            interval.regularMaintenanceItemChoiceName,
          resultValue: undefined,
          comment: "",
          commentExpanded: false,
          recordedBlob: null,
          mediaFiles: [],
        })),
      );

    setForm((prev) => {
      const prevById = new Map(
        prev.maintenanceItems
          .filter(isScheduledMaintenanceItem)
          .map((item) => [item.periodicMaintenanceMachineItemId, item]),
      );
      const freshById = new Map(
        scheduledFromApi.map((item) => [
          item.periodicMaintenanceMachineItemId,
          item,
        ]),
      );

      const reconciled = scheduledFromApi.map((fresh) => {
        const existing = prevById.get(fresh.periodicMaintenanceMachineItemId);
        if (!existing) return fresh;
        return {
          ...fresh,
          id: existing.id,
          maintenanceReportItemResponseId:
            existing.maintenanceReportItemResponseId,
          resultValue: existing.resultValue,
          comment: existing.comment,
          commentExpanded: existing.commentExpanded,
          recordedBlob: existing.recordedBlob,
          mediaFiles: existing.mediaFiles,
          existingItemMedias: existing.existingItemMedias,
          existingItemRecording: existing.existingItemRecording,
        };
      });

      const orphaned =
        mode === "edit" || mode === "draft"
          ? Array.from(prevById.values()).filter(
              (item) => !freshById.has(item.periodicMaintenanceMachineItemId),
            )
          : [];

      return {
        ...prev,
        maintenanceItems: [
          ...prev.maintenanceItems.filter((item) => item.source === "manual"),
          ...reconciled,
          ...orphaned,
        ],
      };
    });
  }, [scheduleItems]);

  useEffect(() => {
    recorderRef.current = recorder;
  }, [recorder]);

  useEffect(() => {
    if (recordingTime >= 60 && isRecording && !stoppedRef.current) {
      stopRecording();
    }
  }, [recordingTime, isRecording, stopRecording]);

  const allMaintenanceItemsPerformed = useMemo(() => {
    if (form.maintenanceItems.length === 0) return false;
    return form.maintenanceItems.every(
      (item) => item.resultValue === MaintenanceResultType.PERFORMED,
    );
  }, [form.maintenanceItems]);

  const handleSelectAllPerformed = useCallback(() => {
    setForm((prev) => {
      const allAlreadyPerformed = prev.maintenanceItems.every(
        (item) => item.resultValue === MaintenanceResultType.PERFORMED,
      );
      return {
        ...prev,
        maintenanceItems: prev.maintenanceItems.map((item) => ({
          ...item,
          resultValue: allAlreadyPerformed
            ? undefined
            : MaintenanceResultType.PERFORMED,
        })),
      };
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isRecording || isPaused || recorderRef.current) {
      stopRecording();
    }
    setOpenCancelConfirm(false);
    setSignatureDialogOpen(false);
    setViewerOpen(false);
    setScheduleItems(null);
    setScheduleItemsLoading(false);
    setEditInitialize(false);
    setIntervalTouched(false);
    setSelectedImage("");
    resetForm();
    onClose();
  }, [isPaused, isRecording, onClose, resetForm, stopRecording]);

  const handleRequestClose = useCallback(() => {
    setOpenCancelConfirm(true);
  }, []);

  const handleDeleteRecordingConfirmed = useCallback(() => {
    if (isRecording || isPaused) {
      skipNextRecordingBlobRef.current = true;
      stopRecording();
    }

    releaseMicrophone();

    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    const pendingItemId = pendingDeleteRecordingItemIdRef.current;
    setForm((prev) => ({
      ...prev,
      maintenanceItems: prev.maintenanceItems.map((item) =>
        item.id === pendingItemId
          ? { ...item, recordedBlob: null, existingItemRecording: null }
          : item,
      ),
    }));
    pendingDeleteRecordingItemIdRef.current = null;

    setRecordingTime(0);
    setIsPaused(false);
    setIsRecording(false);
    setRecorder(null);
    recorderRef.current = null;
    setRecordingInterval(null);
    setMediaAvailable(true);
    setOpenFormDeleteRecording(false);
    recordingStartTimeRef.current = null;
  }, [isRecording, isPaused, stopRecording, releaseMicrophone]);

  const uploadDescription = useMemo(
    () => [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ],
    [t],
  );

  const handleMaintenanceIntervalChange = (
    event: SelectChangeEvent<string[]>,
  ) => {
    const value = event.target.value;
    patchForm({
      maintenanceIntervalIds:
        typeof value === "string" ? value.split(",") : value,
    });
  };

  const maintenanceIntervalRenderValue = (selected: string[]) => {
    if (!selected.length) {
      return "";
    }
    return intervalOptions
      .filter((opt) => selected.includes(opt.id))
      .map((opt) => opt.label)
      .join(", ");
  };

  const isPeriodicMaintenance =
    maintenanceType === MaintenanceReportType.PERIODIC_MAINTENANCE;
  const periodicDropdownsDisabled = masterDataLoading || !!masterDataError;
  const periodicDropdownsValidationReady =
    !masterDataLoading && !masterDataError;
  const itemDropdownsDisabled = masterDataLoading || !!masterDataError;

  const serviceMeterVal = form.serviceMeterValue.trim();
  const odometerVal = form.odometerValue.trim();
  const serviceMeterOverMax =
    !!serviceMeterVal &&
    Number.isFinite(parseFloat(serviceMeterVal)) &&
    parseFloat(serviceMeterVal) > NUMERIC_SMR_MAX;
  const serviceMeterFormatInvalid =
    !!serviceMeterVal && !isNumericValueValid(serviceMeterVal, NUMERIC_SMR_MAX);
  const serviceMeterMissing = !serviceMeterVal;
  const serviceMeterShowError =
    form.serviceMeterTouched &&
    (serviceMeterMissing || serviceMeterOverMax || serviceMeterFormatInvalid);
  const serviceMeterHelperText = serviceMeterOverMax
    ? t("createInspection.numericMax", { max: NUMERIC_SMR_MAX })
    : undefined;

  const serviceMeterInvalid =
    serviceMeterMissing || serviceMeterFormatInvalid || serviceMeterOverMax;

  const odometerOverMax =
    !!odometerVal &&
    Number.isFinite(parseFloat(odometerVal)) &&
    parseFloat(odometerVal) > NUMERIC_ODO_MAX;
  const odometerFormatInvalid =
    !!odometerVal && !isNumericValueValid(odometerVal, NUMERIC_ODO_MAX);
  const odometerShowError =
    form.odometerTouched && (odometerOverMax || odometerFormatInvalid);
  const odometerHelperText = odometerOverMax
    ? t("createInspection.numericMax", { max: NUMERIC_ODO_MAX })
    : undefined;

  const odometerInvalid = odometerFormatInvalid || odometerOverMax;

  const workAtMissing = !form.workAt;
  const workAtOutOfRange = !!form.workAt && !isWorkAtInValidRange(form.workAt);
  const workAtInvalid = workAtMissing || workAtOutOfRange;
  const workAtShowError = form.workAtTouched && workAtInvalid;
  const workAtHelperText = workAtMissing
    ? t("required")
    : workAtOutOfRange
      ? t("createRefuel.workAtOutOfRange")
      : undefined;

  const hasMaintenanceItem = form.maintenanceItems.length > 0;

  const totalMaintenanceItemsMediaCount = useMemo(
    () => countMaintenanceItemsMediaTotal(form.maintenanceItems),
    [form.maintenanceItems],
  );

  const otherItemsMediaCountForEditor = useMemo(
    () =>
      countMaintenanceItemsMediaTotal(
        form.maintenanceItems,
        editingMaintenanceItem?.id,
      ),
    [form.maintenanceItems, editingMaintenanceItem?.id],
  );

  const hasManualItems = form.maintenanceItems.some(
    (item) => item.source === "manual",
  );

  const periodicIntervalMissing =
    isPeriodicMaintenance &&
    form.maintenanceIntervalIds.length === 0 &&
    !((mode === "edit" || mode === "draft") && hasManualItems);

  const periodicFieldsInvalid =
    isPeriodicMaintenance && periodicIntervalMissing;
  const periodicIntervalShowError =
    periodicDropdownsValidationReady &&
    form.periodicFieldsTouched &&
    periodicIntervalMissing;

  const periodicSectionComplete =
    isPeriodicMaintenance &&
    arePeriodicMaintenanceFieldsValid(
      isPeriodicMaintenance,
      form.maintenanceIntervalIds,
    );

  const hasInvalidMaintenanceItems = useMemo(
    () =>
      form.maintenanceItems.some((item) => {
        const resultMissing = !item.resultValue;
        return (
          resultMissing ||
          !!item.commentError ||
          isManualMaintenanceItemRefillAmountInvalid(item)
        );
      }),
    [form.maintenanceItems],
  );

  const hasMissingPeriodicResult = useMemo(
    () =>
      isPeriodicMaintenance &&
      form.maintenanceItems.some((item) => !item.resultValue),
    [form.maintenanceItems, isPeriodicMaintenance],
  );

  const hasMaintenanceContent = hasMaintenanceItem || periodicSectionComplete;

  const isRegisterDisabled =
    isSubmitting ||
    !groupId ||
    !machineId ||
    !hasMaintenanceContent ||
    workAtInvalid ||
    serviceMeterInvalid ||
    odometerInvalid ||
    form.hasUploadError ||
    totalMaintenanceItemsMediaCount > MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT ||
    isRecording ||
    isPaused ||
    hasInvalidMaintenanceItems ||
    periodicIntervalMissing;

  const handleSignatureClose = useCallback(() => {
    setSignatureDialogOpen(false);
  }, []);

  const handleSignatureSend = useCallback(
    async (
      signatureDataUrl: string | null,
      options?: { submitAlreadyStarted?: boolean },
    ) => {
      if (!groupId || !machineId) return;

      if (!options?.submitAlreadyStarted) {
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        setIsSubmitting(true);
      }

      dispatch(setIsLoading(true));
      try {
        const existingReportForUpdate = editingReport
          ? editingReport
          : draftMachineReportId
            ? { machineReportId: draftMachineReportId }
            : null;

        const { successMessage } = existingReportForUpdate
          ? await submitUpdateMaintenance({
              groupId,
              machineId,
              report: existingReportForUpdate,
              signatureDataUrl,
              draft: {
                workAt: form.workAt,
                serviceMeterValue: form.serviceMeterValue,
                odometerValue: form.odometerValue,
                periodicSwitchOn: isPeriodicMaintenance,
                maintenanceIntervalIds: form.maintenanceIntervalIds,
                workScopeId: form.workScopeId,
                maintenanceItems: form.maintenanceItems,
              },
            })
          : await submitCreateMaintenance({
              groupId,
              machineId,
              signatureDataUrl,
              draft: {
                workAt: form.workAt,
                serviceMeterValue: form.serviceMeterValue,
                odometerValue: form.odometerValue,
                periodicSwitchOn: isPeriodicMaintenance,
                maintenanceIntervalIds: form.maintenanceIntervalIds,
                workScopeId: form.workScopeId,
                maintenanceItems: form.maintenanceItems,
              },
            });

        dispatch(setShowAlert(successMessage || t("maintenanceReportNew")));
        dispatch(setIsUpdatedMachineReportResponses(`${Date.now()}`));
        setSignatureDialogOpen(false);
        handleClose();
        if (mode === "edit" || mode === "draft") {
          onUpdateSuccess?.();
        }
      } catch (error) {
        console.error("Failed to create maintenance report:", error);
        const message =
          error instanceof AxiosError
            ? error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage
            : error instanceof Error && error.message === "SIGNATURE_REQUIRED"
              ? t("required")
              : error instanceof Error &&
                  error.message === "PERIODIC_MAINTENANCE_FIELDS_REQUIRED"
                ? t("required")
                : error instanceof Error &&
                    error.message === "MAINTENANCE_ITEMS_MEDIA_TOTAL_EXCEEDED"
                  ? t("maxFilesExceeded", {
                      max: MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
                    })
                  : error instanceof Error &&
                      error.message === "SIGNATURE_UPLOAD_FAILED"
                    ? t("Anunexpectederroroccurredwhileprocessingthefile")
                    : null;
        if (message) {
          dispatch(setShowAlert(message));
        }
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        dispatch(setIsLoading(false));
      }
    },
    [
      dispatch,
      draftMachineReportId,
      form.maintenanceIntervalIds,
      form.maintenanceItems,
      form.odometerValue,
      form.serviceMeterValue,
      form.workAt,
      form.workScopeId,
      groupId,
      handleClose,
      machineId,
      t,
    ],
  );

  const handleSaveAsDraft = useCallback(async () => {
    if (!groupId || !machineId) return;
    if (submitInFlightRef.current) return;

    if (isPeriodicMaintenance) {
      if (periodicIntervalMissing) {
        if (periodicDropdownsValidationReady) {
          patchForm({ periodicFieldsTouched: true });
        }
        return;
      }
      const missingResultItem = form.maintenanceItems.find(
        (item) => !item.resultValue,
      );
      if (missingResultItem) {
        setValidationAttempted(true);
        dispatch(setShowAlert(t("required")));
        requestAnimationFrame(() => {
          itemRefs.current[missingResultItem.id]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
        return;
      }
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    dispatch(setIsLoading(true));
    try {
      if (isRecording || isPaused || recorderRef.current) {
        stopRecording();
      }

      const draftFields = {
        workAt: form.workAt,
        serviceMeterValue: form.serviceMeterValue,
        odometerValue: form.odometerValue,
        periodicSwitchOn: isPeriodicMaintenance,
        maintenanceIntervalIds: form.maintenanceIntervalIds,
        workScopeId: form.workScopeId,
        maintenanceItems: form.maintenanceItems,
      };

      const existingMachineReportId =
        draftMachineReportId ?? editingReport?.machineReportId ?? null;

      const { successMessage, machineReportId } = existingMachineReportId
        ? await submitUpdateMaintenance({
            groupId,
            machineId,
            report: {
              machineReportId: existingMachineReportId,
              maintenanceReportId: editingReport?.maintenanceReportId,
            },
            draft: draftFields,
            signatureDataUrl: null,
            saveAsDraft: true,
          })
        : await submitCreateMaintenanceDraft({
            groupId,
            machineId,
            draft: draftFields,
          });

      if (machineReportId) {
        setDraftMachineReportId(machineReportId);
      }

      dispatch(setShowAlert(successMessage || t("maintenanceReportNew")));
      dispatch(setIsUpdatedMachineReportResponses(`${Date.now()}`));
      handleClose();
      onUpdateSuccess?.();
    } catch (error) {
      console.error("Failed to save maintenance report draft:", error);
      const message =
        error instanceof AxiosError
          ? error.response?.data?.error?.message ||
            error.response?.data?.meta?.errorMessage
          : error instanceof Error && error.message === "WORK_AT_INVALID"
            ? t("required")
            : error instanceof Error &&
                error.message === "MAINTENANCE_ITEMS_MEDIA_TOTAL_EXCEEDED"
              ? t("maxFilesExceeded", {
                  max: MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT,
                })
              : null;
      if (message) {
        dispatch(setShowAlert(message));
      }
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      dispatch(setIsLoading(false));
    }
  }, [
    dispatch,
    draftMachineReportId,
    editingReport,
    form.maintenanceIntervalIds,
    form.maintenanceItems,
    form.odometerValue,
    form.serviceMeterValue,
    form.workAt,
    form.workScopeId,
    groupId,
    handleClose,
    isPaused,
    isPeriodicMaintenance,
    isRecording,
    machineId,
    onUpdateSuccess,
    patchForm,
    periodicDropdownsValidationReady,
    periodicIntervalMissing,
    stopRecording,
    t,
  ]);

  const isSaveAsDraftDisabled =
    isSubmitting ||
    !groupId ||
    !machineId ||
    isRecording ||
    isPaused ||
    hasMissingPeriodicResult ||
    periodicIntervalMissing;

  const handleOpenSignatureDialog = useCallback(async () => {
    if (!groupId || !machineId) return;
    if (submitInFlightRef.current) return;

    if (periodicFieldsInvalid) {
      if (periodicDropdownsValidationReady) {
        patchForm({ periodicFieldsTouched: true });
      }
      return;
    }

    if (isRegisterDisabled) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      if (isRecording || isPaused || recorderRef.current) {
        stopRecording();
      }

      const isSignatureRequired = await getGroupIsSignatureRequired(groupId);
      if (isSignatureRequired) {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        setSignatureDialogOpen(true);
        return;
      }

      await handleSignatureSend(null, { submitAlreadyStarted: true });
    } catch (error) {
      console.error("Failed to open signature dialog:", error);
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    groupId,
    handleSignatureSend,
    isPaused,
    isRecording,
    isRegisterDisabled,
    machineId,
    patchForm,
    periodicDropdownsValidationReady,
    periodicFieldsInvalid,
    stopRecording,
  ]);

  const handleUploadPreview = (url: string | string[]) => {
    const u = Array.isArray(url) ? url[0] : url;
    setSelectedImage(u);
    setViewerOpen(true);
  };

  const handleCloseAddMaintenanceItemDialog = () => {
    setOpenAddMaintenanceItemDialog(false);
    setAddItemCategoryId("");
  };

  const handleAddItemCategoryChange = (event: SelectChangeEvent<string>) => {
    setAddItemCategoryId(event.target.value);
  };

  const addItemCategoryRenderValue = (selected: string) => {
    if (!selected) {
      return "";
    }
    return (
      categoryOptions.find((opt) => opt.id === selected)?.label ?? selected
    );
  };

  const handleConfirmAddMaintenanceItem = () => {
    setPresetItemEditorCategoryId(addItemCategoryId ?? "");
    setEditingMaintenanceItem(null);
    setOpenAddMaintenanceItemDialog(false);
    setAddItemCategoryId("");
    setOpenMaintenanceItemEditor(true);
  };

  const handleSaveMaintenanceItem = (entry: MaintenanceItemEntry) => {
    setForm((prev) => {
      const isNewItem = !prev.maintenanceItems.some(
        (item) => item.id === entry.id,
      );
      const nextEntry =
        isNewItem && !entry.resultValue
          ? { ...entry, resultValue: MaintenanceResultType.PERFORMED }
          : entry;
      return {
        ...prev,
        maintenanceItems: isNewItem
          ? [...prev.maintenanceItems, nextEntry]
          : prev.maintenanceItems.map((item) =>
              item.id === nextEntry.id ? nextEntry : item,
            ),
      };
    });
    setEditingMaintenanceItem(null);
    setPresetItemEditorCategoryId("");
  };

  const handleDeleteMaintenanceItem = () => {
    if (!editingMaintenanceItem) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      maintenanceItems: prev.maintenanceItems.filter(
        (item) => item.id !== editingMaintenanceItem.id,
      ),
    }));
    setEditingMaintenanceItem(null);
    setPresetItemEditorCategoryId("");
  };

  const handleRemoveMaintenanceItem = useCallback((itemId: string) => {
    setForm((prev) => ({
      ...prev,
      maintenanceItems: prev.maintenanceItems.filter(
        (item) => !(item.id === itemId && item.source === "manual"),
      ),
    }));
  }, []);

  const handleCloseMaintenanceItemEditor = () => {
    setOpenMaintenanceItemEditor(false);
    setEditingMaintenanceItem(null);
    setPresetItemEditorCategoryId("");
  };

  return (
    <>
      <DataDialog
        open={open}
        onClose={handleRequestClose}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {t("maintenanceReport")}

            <span style={{ color: "grey", fontSize: "12px", fontWeight: 500 }}>
              *{t("required")}
            </span>
          </div>
        }
        fullWidth
        disableEnforceFocus
        className="dialog-container"
        dialogContentSx={{
          overflow: "auto",
          maxHeight: "min(70vh, 720px)",
          touchAction: "auto",
        }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleRequestClose}
              disabled={isSubmitting}
            >
              {t("dialogReport.cancel")}
            </Button>
            {/* Save as Draft button hidden — feature deferred to Phase 5 (draft sharing under consideration), implementation kept as-is. */}
            {false && mode !== "edit" && (
              <Button
                className="btn btn-no-border btn-save-draft"
                style={{ width: "auto" }}
                onClick={handleSaveAsDraft}
                disabled={isSaveAsDraftDisabled}
              >
                {t("saveDraft")}
              </Button>
            )}
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              onClick={handleOpenSignatureDialog}
              disabled={isRegisterDisabled}
            >
              {t("register")}
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}>
          {isPeriodicMaintenance && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {masterDataError && (
                <Typography color="error" variant="body2">
                  {masterDataError}
                </Typography>
              )}

              <FormControl
                fullWidth
                className="select-field"
                sx={MAINTENANCE_SELECT_FORM_CONTROL_SX}
                disabled={periodicDropdownsDisabled}
                error={periodicIntervalShowError}
                required
              >
                <InputLabel id="maintenance-interval-select-label">
                  {t("dialogMaintenanceReports.maintenanceInterval")}
                </InputLabel>
                <Select
                  labelId="maintenance-interval-select-label"
                  id="maintenance-interval-select"
                  label={t("dialogMaintenanceReports.maintenanceInterval")}
                  value={form.maintenanceIntervalIds}
                  onChange={handleMaintenanceIntervalChange}
                  onClose={() => {
                    if (periodicDropdownsValidationReady) {
                      patchForm({ periodicFieldsTouched: true });
                    }
                  }}
                  renderValue={maintenanceIntervalRenderValue}
                  MenuProps={{
                    ...MAINTENANCE_SELECT_MENU_PROPS,
                    PaperProps: {
                      ...MAINTENANCE_SELECT_MENU_PROPS.PaperProps,
                      sx: {
                        ...MAINTENANCE_SELECT_MENU_PROPS.PaperProps.sx,
                        "& .MuiMenuItem-root": {
                          minHeight: 40,
                          py: 0.5,
                        },
                      },
                    },
                  }}
                >
                  {intervalOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id} dense>
                      <ListItemText primary={opt.label} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box>
            <Typography
              variant="subtitle1"
              className="txt-black-1"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {t("createMaintenance.maintenanceItems")}
            </Typography>
            {isPeriodicMaintenance ? (
              scheduleItemsLoading ? (
                <MaintenanceScheduleItemsSkeleton />
              ) : (
                itemGroups.map((group) => (
                  <Box key={group.intervalId} sx={{ mb: 3 }}>
                    <Box
                      className="mb-10"
                      sx={{
                        width: "100%",
                        bgcolor: "#e5e7fa",
                        color: "#00017d",
                        fontWeight: "bold",
                        py: 1.25,
                        px: 1.5,
                      }}
                    >
                      {formatIntervalMaintenanceLabel(
                        group.code,
                        group.name,
                        isoLocaleCode,
                        t("maintenanceReport"),
                      )}
                    </Box>
                    {group.items.map((item, index) => (
                      <Box
                        key={item.id}
                        sx={{
                          borderRadius: 1,
                          bgcolor: "#fff",
                          py: 2,
                          px: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                          alignItems: "stretch",
                        }}
                      >
                        <MaintenanceNonNumericItemRow
                          item={item}
                          itemIndex={index}
                          validationAttempted={validationAttempted}
                          mediaAvailable={mediaAvailable}
                          isRecording={isRecording}
                          isPaused={isPaused}
                          recordingForItemId={recordingForItemId}
                          recordingStartTimeMs={recordingStartTimeMs}
                          totalInspectionMediaCount={totalInspectionMediaCount}
                          mediaPreviewNonce={mediaPreviewNonce}
                          uploadDescription={uploadDescription}
                          onPatchDraft={patchDraft}
                          onToggleCommentSection={handleToggleCommentSection}
                          onResultIconPick={handleResultIconPick}
                          onStartRecording={startRecordingForItem}
                          onStopRecording={stopRecording}
                          onRequestDeleteRecording={requestDeleteRecording}
                          onPreviewTemplateMedia={openTemplateMediaPreview}
                          onPreviewUploadMedia={handleUploadPreview}
                          onMediaFilesChange={handleMediaFilesChange}
                          onFileCountChange={handleFileCountChange}
                          onUploadErrorChange={handleUploadErrorChange}
                          onRemoveItem={handleRemoveMaintenanceItem}
                          setItemRef={setItemRef}
                        />
                      </Box>
                    ))}
                  </Box>
                ))
              )
            ) : (
              form.maintenanceItems.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    borderRadius: 1,
                    bgcolor: "#fff",
                    py: 2,
                    px: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    alignItems: "stretch",
                  }}
                >
                  <MaintenanceNonNumericItemRow
                    item={item}
                    itemIndex={index}
                    validationAttempted={validationAttempted}
                    mediaAvailable={mediaAvailable}
                    isRecording={isRecording}
                    isPaused={isPaused}
                    recordingForItemId={recordingForItemId}
                    recordingStartTimeMs={recordingStartTimeMs}
                    totalInspectionMediaCount={totalInspectionMediaCount}
                    mediaPreviewNonce={mediaPreviewNonce}
                    uploadDescription={uploadDescription}
                    onPatchDraft={patchDraft}
                    onToggleCommentSection={handleToggleCommentSection}
                    onResultIconPick={handleResultIconPick}
                    onStartRecording={startRecordingForItem}
                    onStopRecording={stopRecording}
                    onRequestDeleteRecording={requestDeleteRecording}
                    onPreviewTemplateMedia={openTemplateMediaPreview}
                    onPreviewUploadMedia={handleUploadPreview}
                    onMediaFilesChange={handleMediaFilesChange}
                    onFileCountChange={handleFileCountChange}
                    onUploadErrorChange={handleUploadErrorChange}
                    onRemoveItem={handleRemoveMaintenanceItem}
                    setItemRef={setItemRef}
                  />
                </Box>
              ))
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                pt: 2,
              }}
            >
              <Button
                className="btn btn-secondary"
                startIcon={
                  <IconPlusBlack
                    fillColor={itemDropdownsDisabled ? "#9e9e9e" : "black"}
                  />
                }
                onClick={handleConfirmAddMaintenanceItem}
                disabled={itemDropdownsDisabled}
                sx={{ minWidth: 220 }}
              >
                {t("formAction.add")}
              </Button>
              {!(isPeriodicMaintenance && scheduleItemsLoading) && (
                <Box
                  sx={{
                    borderRadius: 1,
                    bgcolor: "#f7f9ff",
                    py: 2,
                    px: 2,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.6,
                      textAlign: "center",
                    }}
                  >
                    {periodicScheduleAvailable === false ||
                    (intervalTouched &&
                      form.maintenanceItems.filter(isScheduledMaintenanceItem)
                        .length === 0)
                      ? t("dialogMaintenanceReports.noScheduleAvailableNotice")
                      : t("dialogMaintenanceReports.standardItemsNotice")}
                  </Typography>
                </Box>
              )}
              {form.maintenanceItems.length > 0 && (
                <Button
                  className="btn btn-secondary"
                  startIcon={<CheckIcon />}
                  onClick={handleSelectAllPerformed}
                  sx={{ minWidth: 220 }}
                >
                  {allMaintenanceItemsPerformed
                    ? t("formAction.deselectAll")
                    : t("selectAll")}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                "& .MuiFormControl-root": {
                  width: "100%",
                },
              }}
            >
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={dateLocale}
              >
                <DateTimePicker
                  label={t("dialogMaintenanceReports.workTime")}
                  className="input-field"
                  format={userTimeFormat}
                  value={form.workAt ?? undefined}
                  minDateTime={WORK_AT_MIN_DATE}
                  maxDateTime={new Date()}
                  onChange={(value) =>
                    patchForm({
                      workAt: value ?? undefined,
                      workAtTouched: true,
                    })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: workAtShowError,
                      helperText: workAtHelperText,
                      onBlur: () => patchForm({ workAtTouched: true }),
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                className={`input-field ${
                  serviceMeterShowError ? "error-border" : ""
                }`}
                label={t("serviceMeterOrSMR")}
                type="text"
                inputMode="decimal"
                fullWidth
                variant="outlined"
                required
                value={form.serviceMeterValue}
                onChange={(e) => {
                  const validatedInput = sanitizeNumericInputValue(
                    e.target.value,
                    form.serviceMeterValue,
                  );
                  patchForm({
                    serviceMeterValue: validatedInput,
                    serviceMeterTouched: true,
                  });
                }}
                onBlur={() => {
                  const normalized = normalizeNumericOnBlur(
                    form.serviceMeterValue,
                    NUMERIC_SMR_MAX,
                  );
                  patchForm({
                    serviceMeterTouched: true,
                    ...(normalized !== form.serviceMeterValue
                      ? { serviceMeterValue: normalized }
                      : {}),
                  });
                }}
                error={serviceMeterShowError}
                helperText={serviceMeterHelperText}
                FormHelperTextProps={{
                  className: serviceMeterShowError ? "error-text" : undefined,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">h</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    textAlign: "right",
                    direction: "ltr",
                    paddingRight: "8px",
                  },
                }}
                inputProps={{
                  step: "0.1",
                  min: "0",
                }}
              />

              <TextField
                className={`input-field ${
                  odometerShowError ? "error-border" : ""
                }`}
                label={getOdometerLabel(t)}
                type="text"
                inputMode="decimal"
                fullWidth
                variant="outlined"
                value={form.odometerValue}
                onChange={(e) => {
                  const validatedInput = sanitizeNumericInputValue(
                    e.target.value,
                    form.odometerValue,
                  );
                  patchForm({
                    odometerValue: validatedInput,
                    odometerTouched: true,
                  });
                }}
                onBlur={() => {
                  const normalized = normalizeNumericOnBlur(
                    form.odometerValue,
                    NUMERIC_ODO_MAX,
                  );
                  patchForm({
                    odometerTouched: true,
                    ...(normalized !== form.odometerValue
                      ? { odometerValue: normalized }
                      : {}),
                  });
                }}
                error={odometerShowError}
                helperText={odometerHelperText}
                FormHelperTextProps={{
                  className: odometerShowError ? "error-text" : undefined,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">km</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    textAlign: "right",
                    direction: "ltr",
                    paddingRight: "8px",
                  },
                }}
                inputProps={{
                  step: "0.1",
                  min: "0",
                }}
              />
            </Box>
          </Box>
        </Box>
      </DataDialog>

      <DataDialog
        open={openCancelConfirm}
        setOpen={setOpenCancelConfirm}
        disableBackdropClick={true}
      >
        <Typography variant="body1" sx={{ mb: 3, fontWeight: 700 }}>
          {t("cancelAddingGroup")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => setOpenCancelConfirm(false)}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button className="btn btn-primary" onClick={handleClose}>
            {t("ok")}
          </Button>
        </Box>
      </DataDialog>

      <DataDialog
        title={t("errNetwork.title")}
        open={openFormNoMic}
        setOpen={setOpenFormNoMic}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("noMic")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormNoMic(false)}
              className="btn btn-primary ml-8"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("deleteRecording")}
        open={openFormDeleteRecording}
        setOpen={setOpenFormDeleteRecording}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDeleteRecording(false)}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={handleDeleteRecordingConfirmed}
              className="btn-delete"
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("dialogMaintenanceReports.item")}
        open={openAddMaintenanceItemDialog}
        setOpen={setOpenAddMaintenanceItemDialog}
        onClose={handleCloseAddMaintenanceItemDialog}
        disableBackdropClick={true}
        disableEnforceFocus
        dialogContentSx={{ overflow: "visible", pt: 2 }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleCloseAddMaintenanceItemDialog}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              onClick={handleConfirmAddMaintenanceItem}
              disabled={!addItemCategoryId}
            >
              {t("formAction.add")}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 2, pb: 1, overflow: "visible" }}>
          {masterDataError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {masterDataError}
            </Typography>
          )}
          <FormControl
            fullWidth
            className="select-field"
            sx={MAINTENANCE_SELECT_FORM_CONTROL_SX}
            disabled={itemDropdownsDisabled}
          >
            <InputLabel id="maintenance-item-category-select-label">
              {t("dialogMaintenanceReports.category")}
            </InputLabel>
            <Select
              labelId="maintenance-item-category-select-label"
              id="maintenance-item-category-select"
              label={t("dialogMaintenanceReports.category")}
              value={addItemCategoryId}
              onChange={handleAddItemCategoryChange}
              renderValue={addItemCategoryRenderValue}
              MenuProps={MAINTENANCE_SELECT_MENU_PROPS}
            >
              {categoryOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DataDialog>

      <MaintenanceItemEditorDialog
        open={openMaintenanceItemEditor}
        onClose={handleCloseMaintenanceItemEditor}
        onSave={handleSaveMaintenanceItem}
        onDelete={
          editingMaintenanceItem ? handleDeleteMaintenanceItem : undefined
        }
        initialEntry={editingMaintenanceItem}
        presetCategoryId={presetItemEditorCategoryId}
        categoryOptions={categoryOptions}
        maintenanceCategories={maintenanceCategories}
        maintenanceTypes={maintenanceTypes}
        irregularMaintenanceItems={irregularMaintenanceItems}
        masterDataLoading={masterDataLoading}
        masterDataError={masterDataError}
        otherItemsMediaCount={otherItemsMediaCountForEditor}
      />

      <SignatureDialog
        open={signatureDialogOpen}
        onClose={handleSignatureClose}
        onSend={handleSignatureSend}
        isSubmitting={isSubmitting}
      />

      {typeof document !== "undefined" &&
        viewerOpen &&
        selectedImage &&
        createPortal(
          <CustomImageViewer
            imageUrl={selectedImage}
            onClose={() => {
              setViewerOpen(false);
              setSelectedImage("");
            }}
          />,
          document.body,
        )}
    </>
  );
}
